import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { PauseCircle, PlayCircle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * KillSwitchCard — Global LLM pause toggle for super admins.
 * Reads /api/platform/llm-status, toggles via /llm-pause and /llm-resume.
 */
export default function KillSwitchCard({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/platform/llm-status`, { headers });
      setStatus(data);
    } catch (e) {
      console.error("kill-switch status error:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const togglePause = async () => {
    setLoading(true);
    try {
      const endpoint = status?.paused ? "/platform/llm-resume" : "/platform/llm-pause";
      const { data } = await axios.post(`${API}${endpoint}`, {}, { headers });
      setStatus(data);
      toast.success(data.paused ? "LLM mis en pause ✋" : "LLM réactivé ✅");
      setConfirming(false);
    } catch (e) {
      toast.error("Erreur lors du basculement");
    } finally {
      setLoading(false);
    }
  };

  const paused = status?.paused === true;
  const updatedAt = status?.updated_at ? new Date(status.updated_at).toLocaleString("fr-FR") : "—";
  const updatedBy = status?.updated_by || "—";

  return (
    <Card className={paused ? "border-red-500/50 bg-red-500/5" : "border-green-500/30 bg-green-500/5"}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <ShieldAlert className={`w-4 h-4 ${paused ? "text-red-500" : "text-green-500"}`} />
          Kill switch LLM
          <Badge variant={paused ? "destructive" : "outline"} className="text-[10px]" data-testid="kill-switch-status-badge">
            {paused ? "EN PAUSE" : "ACTIF"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 text-xs text-muted-foreground space-y-1">
            <p>
              {paused
                ? "🛑 Tous les appels LLM sont bloqués. Aucun coût ne s'accumule."
                : "✅ Les appels LLM sont autorisés (chats, résumés, transcriptions…)."}
            </p>
            <p className="text-[10px]">Dernier changement : {updatedAt} par {updatedBy}</p>
          </div>
          {confirming ? (
            <div className="flex flex-col gap-1.5">
              <Button
                size="sm"
                variant={paused ? "default" : "destructive"}
                disabled={loading}
                onClick={togglePause}
                data-testid="kill-switch-confirm-button"
              >
                {paused ? <PlayCircle className="w-4 h-4 mr-1" /> : <PauseCircle className="w-4 h-4 mr-1" />}
                {paused ? "Confirmer réactivation" : "Confirmer pause"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Annuler</Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant={paused ? "default" : "outline"}
              onClick={() => setConfirming(true)}
              disabled={loading || !status}
              data-testid="kill-switch-toggle-button"
              className={paused ? "bg-green-600 hover:bg-green-700" : ""}
            >
              {paused ? <PlayCircle className="w-4 h-4 mr-1" /> : <PauseCircle className="w-4 h-4 mr-1" />}
              {paused ? "Réactiver LLM" : "Mettre en pause"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
